import styled, { css, DefaultTheme } from "styled-components";
import { isContrast } from "core/utils/color-helper";
import CrossSvg from "core/icons/cross/cross2.svg?react";
import { SpacingStyle, getMarginStyle, getPaddingStyle } from "core/styles/paddingMarginStyle";
import { fontSizing } from "core/constants/sizing";
import Breakpoints from "core/constants/breakpoints";

const colorSelector = ({
  color,
  textColor,
  theme,
}: {
  color?: string;
  textColor?: string;
  theme: DefaultTheme;
}) => {
  let cssStyle;

  const findPerfectContrastColor = (
    key: "secondary" | "primary",
    referenceKey: "surface" | "default" | "hover" | "pressed" | "focus"
  ) => {
    const shades = ["default", "shade1", "shade2", "shade3", "shade4"] as const;
    const fallbackShade = shades[shades.length - 1];
    const colorRatio = 2;

    const matchedShade =
      shades.find((shade) =>
        isContrast(theme?.colors?.[key]?.[referenceKey], theme?.colors?.[key]?.[shade], colorRatio)
      ) ?? fallbackShade;

    return theme?.colors?.secondary?.[matchedShade];
  };

  switch (color) {
    case "success":
      cssStyle = css`
        background-color: ${({ theme }) => theme?.colors?.tertiary.tint4};
        color: ${({ theme }) => theme?.colors?.success.main};
      `;
      break;

    case "success-2":
      cssStyle = css`
        background-color: ${({ theme }) => theme?.colors?.success.surface};
        color: ${({ theme }) => theme?.colors?.success.main};
        border: 1px solid ${({ theme }) => theme?.colors?.success.border2};
      `;
      break;

    case "success-3":
      cssStyle = css`
        background-color: ${({ theme }) => theme?.colors?.success.main};
        color: ${({ theme }) => theme?.colors?.neutral.neutral10};
      `;
      break;

    case "warning":
      cssStyle = css`
        background-color: ${({ theme }) => theme?.colors?.secondary.tint4};
        color: ${({ theme }) => theme?.colors?.secondary.shade4};
      `;
      break;

    case "warning-2":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.white};
        background-color: ${({ theme }) => theme?.colors?.warningv2.main};
      `;
      break;

    case "warning-3":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.warningv2.main};
        background-color: ${({ theme }) => theme?.colors?.warningv2.focus};
      `;
      break;

    case "warning-4":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.warningv2.main};
        background-color: ${({ theme }) => theme?.colors?.warningv2.surface};
        border: 1px solid ${({ theme }) => theme?.colors?.warningv2.main};
      `;
      break;

    case "warning-5":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.warningv2.main};
        background-color: ${({ theme }) => theme?.colors?.warningv2.surface};
      `;
      break;

    case "danger":
      cssStyle = css`
        background-color: ${({ theme }) => theme?.colors?.danger.default};
        color: ${({ theme }) => theme?.colors?.white};
      `;
      break;

    case "danger-2":
      cssStyle = css`
        background-color: ${({ theme }) => theme?.colors?.danger.surface};
        color: ${({ theme }) => theme?.colors?.danger.default};
        border: 1px solid ${({ theme }) => theme?.colors?.danger.default};
      `;
      break;

    case "danger-3":
      cssStyle = css`
        background-color: ${({ theme }) => theme?.colors?.danger.surface};
        color: ${({ theme }) => theme?.colors?.danger.default};
        border: 1px solid transparent;
      `;
      break;

    case "danger-4":
      cssStyle = css`
        background-color: ${({ theme }) => theme?.colors?.danger.surface};
        color: ${({ theme }) => theme?.colors?.danger.default};
        border: 1px solid ${({ theme }) => theme?.colors?.danger.border};
      `;
      break;

    case "primary":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.primary.default};
        background-color: ${({ theme }) => theme?.colors?.primary.surface};
      `;
      break;

    case "primary-2":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.white};
        background-color: ${({ theme }) => theme?.colors?.primary.default};
      `;
      break;

    case "primary-3":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.primary.default};
        background-color: ${({ theme }) => theme?.colors?.primary.surface};
        border: 1px solid ${({ theme }) => theme?.colors?.primary.default};
      `;
      break;

    case "primary-4":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.primary.default};
        background-color: ${({ theme }) => theme?.colors?.primary.surface};
        border: 1px solid ${({ theme }) => theme?.colors?.primary.border};
      `;
      break;

    case "secondary":
      cssStyle = css`
        color: ${findPerfectContrastColor("secondary", "surface")};
        background-color: ${({ theme }) => theme?.colors?.secondary.surface};
      `;
      break;

    case "secondary-2":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.secondary.shade3};
        background-color: ${({ theme }) => theme?.colors?.secondary.surface};
      `;
      break;

    case "secondary-3":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.secondary.hover};
        background-color: ${({ theme }) => theme?.colors?.secondary.surface};
        border: 1px solid ${({ theme }) => theme?.colors?.secondary.border};
      `;
      break;

    case "secondary-4":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.white};
        background-color: ${({ theme }) => theme?.colors?.secondary.hover};
      `;
      break;

    case "secondary-5":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.white};
        background-color: ${({ theme }) => theme?.colors?.secondary.pressed};
      `;
      break;

    case "primitives":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.gray.gray70};
        background-color: ${({ theme }) => theme?.colors?.white};
        border: 2px solid ${({ theme }) => theme?.colors?.neutral.neutral60};
      `;
      break;

    case "primitives-2":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.neutral.neutral90};
        background-color: ${({ theme }) => theme?.colors?.white};
        border: 1px solid ${({ theme }) => theme?.colors?.neutral.neutral40};
      `;
      break;

    case "primitives-3":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.primary.default};
        background-color: ${({ theme }) => theme?.colors?.white};
        border: 1px solid ${({ theme }) => theme?.colors?.primary.default};
      `;
      break;

    case "primitives-4":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.neutral.neutral10};
        background-color: ${({ theme }) => theme?.colors?.neutral.neutral90};
        border: 1px solid ${({ theme }) => theme?.colors?.neutral.neutral90};
      `;
      break;

    case "default":
      cssStyle = css`
        background-color: ${({ theme }) => theme?.colors?.gray.gray10};
        color: ${({ theme }) => theme?.colors?.neutral.neutral80};
      `;
      break;

    case "green":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.primitives.green500};
        background-color: ${({ theme }) => theme?.colors?.white};
        border: 1px solid ${({ theme }) => theme?.colors?.primitives.green200};
      `;
      break;

    case "disabled":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.neutral?.neutral60};
        background-color: ${({ theme }) => theme?.colors?.neutral?.neutral30};
      `;
      break;

    case "disabled-2":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.neutral?.neutral60};
        background-color: ${({ theme }) => theme?.colors?.neutral?.neutral30};
        border: 1px solid ${({ theme }) => theme?.colors?.neutral.neutral40};
      `;
      break;

    case "neutral":
      cssStyle = css`
        color: ${({ theme }) => theme?.colors?.neutral.neutral90};
        background-color: ${({ theme }) => theme?.colors?.neutral.neutral20};
        border: 1px solid ${({ theme }) => theme?.colors?.neutral?.neutral40};
      `;
      break;

    default:
      cssStyle = css`
        background-color: ${color};
        color: ${({ theme }) => theme?.colors?.neutral.neutral80};
      `;
      break;
  }

  if (textColor) {
    cssStyle = cssStyle.concat(css`
      color: ${textColor};
    `);
  }

  return cssStyle;
};

type Container = {
  isWrapText: boolean;
  width: string;
  height: string;
  rounded: boolean;
  size: number | string;
  lineHeight: string;
  whiteSpace: string;
  bold: boolean;
  border: string;
} & SpacingStyle;

export const Container = styled.div<Container>`
  display: flex;
  width: ${({ width }) => width || "fit-content"};
  height: ${({ height }) => height || "auto"};
  align-items: center;
  border-radius: ${({ rounded }) => (rounded ? "60px" : "4px")};
  padding: 1px 8px;
  font-family: ${({ bold }) => (bold ? "Nunito-Sans-Bold" : "Nunito-Sans")};
  cursor: ${({ onClick }) => (onClick ? "pointer" : "inherit")};
  font-size: ${({ size = "m" }) =>
    typeof size === "string" ? fontSizing[Breakpoints.desktop4]?.[size] : `${size || 14}px`};
  line-height: ${({ lineHeight }) => lineHeight || "unset"};
  white-space: ${({ whiteSpace }) => whiteSpace || "nowrap"};

  ${({ isWrapText }) => {
    if (isWrapText)
      return css`
        justify-content: space-between;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        position: relative;
        display: block;
      `;
  }}

  ${getPaddingStyle}
  ${getMarginStyle}
  ${colorSelector}

  ${({ border }) => {
    if (border) {
      return css`
        border: ${border};
      `;
    }
  }}

  @media screen and (max-width: ${Breakpoints.tablet}px) {
    font-size: ${({ size = "m" }) =>
      typeof size === "string" ? fontSizing[Breakpoints.tablet]?.[size] : `${size || 14}px`};
  }

  @media screen and (max-width: ${Breakpoints.mobile3}px) {
    font-size: ${({ size = "m" }) =>
      typeof size === "string" ? fontSizing[Breakpoints.mobile3]?.[size] : `${size || 14}px`};
  }
`;

export const CrossIcon = styled(CrossSvg)`
  width: 16px;
  height: 16px;
  margin-left: 8px;
  flex-shrink: 0;
  cursor: pointer;

  path {
    stroke: ${({ strokeColor, theme }) => strokeColor ?? theme?.colors?.primary.default};
  }
`;
